import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCustomerWarehouseCutoff } from "../../store/slices/CustomerSlice/CustomerSlice";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function RemainingTime() {
  const [timeInfo, setTimeInfo] = useState({
    timeLeft: "",
    cutoffPassed: false,
    currentTime: new Date(),
  });

  const { customerWarehouseCutoff } = useSelector((state) => state.customer);
  const dispatch = useDispatch();

  const first = useRef(true);

  useEffect(() => {
    if (first.current) {
      dispatch(getCustomerWarehouseCutoff());
      first.current = false;
    }

    const interval = setInterval(() => {
      const cutoffTimeStr = customerWarehouseCutoff?.cutoff || "5:00 pm";

      // Get "now" in Eastern Time
      const nowET = new Date(
        new Date().toLocaleString("en-US", {
          timeZone: "America/New_York",
        })
      );

      // Parse the cutoff time string
      const [time, meridian] = cutoffTimeStr.toLowerCase().split(" ");
      const [hourStr, minuteStr] = time.split(":");
      let hours = parseInt(hourStr, 10);
      const minutes = parseInt(minuteStr, 10);

      if (meridian === "pm" && hours !== 12) hours += 12;
      if (meridian === "am" && hours === 12) hours = 0;

      // Build today's cutoff time in ET
      const cutoff = new Date(nowET);
      cutoff.setHours(hours, minutes, 0, 0);

      const diffMs = cutoff - nowET;

      let timeLeft = "";
      let cutoffPassed = false;

      if (diffMs < 0) {
        cutoffPassed = true;
      } else {
        const totalMinutes = Math.floor(diffMs / 60000);
        const h = Math.floor(totalMinutes / 60);
        const m = totalMinutes % 60;

        if (h > 0) timeLeft += `${h}h `;
        timeLeft += `${m}m`;
      }

      setTimeInfo({
        timeLeft: timeLeft.trim(),
        cutoffPassed,
        currentTime: nowET,
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [customerWarehouseCutoff?.cutoff, dispatch]);

  return (
    <div className="mb-6">
      {timeInfo.cutoffPassed ? (
        // After cutoff message
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-amber-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.236 4.53L8.179 10.5a.75.75 0 00-1.358.636l1.5 3.2a.75.75 0 001.269.044l4-5.6z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-amber-800">
                Today's cutoff time ({customerWarehouseCutoff?.cutoff}) has
                passed
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Orders placed now will be processed next business day
              </p>
            </div>
          </div>
          <div className="mt-2 text-xs text-amber-600">
            Current time: {timeInfo.currentTime.toLocaleTimeString()}
          </div>
        </div>
      ) : (
        // Before cutoff message - modern design
        <div className="rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-green-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.236 4.53L8.179 10.5a.75.75 0 00-1.358.636l1.5 3.2a.75.75 0 001.269.044l4-5.6z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <div className="flex items-baseline gap-2">
                  <p className="text-sm font-medium text-green-800">
                    Order in the next
                  </p>
                  <span className="inline-flex items-center rounded-md bg-green-100 px-2.5 py-0.5 text-sm font-semibold text-green-800 ring-1 ring-inset ring-green-600/20">
                    {timeInfo.timeLeft}
                  </span>
                  <p className="text-sm font-medium text-green-800">
                    for same-day shipping!
                  </p>
                </div>
                <p className="text-sm text-green-700 mt-1">
                  Cutoff time: {customerWarehouseCutoff?.cutoff}
                </p>
              </div>
            </div>
            <div className="flex-shrink-0">
              <div className="text-xs text-green-600 text-right">
                <div>Current time</div>
                <div className="font-mono">
                  {timeInfo.currentTime.toLocaleTimeString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
